import styled, { css } from 'styled-components'

type Direction = 'row' | 'column' | 'row-reverse'
type Justify = 'flex-start' | 'flex-end' | 'center' | 'space-between' | 'space-around' | 'space-evenly'
type Align = 'stretch' | 'start' | 'center' | 'end'

const gridColumnCount = 12

interface GridProps {
  direction?: Direction
  gap?: number
  justify?: Justify
  align?: Align
  fullwidth?: boolean
  fullHeight?: boolean
  noWrap?: boolean
  style?: React.CSSProperties
}

const Root = styled.div<GridProps>`
  position: relative;
  display: flex;
  flex-direction: ${props => props.direction};
  flex-wrap: ${props => props.noWrap ? 'nowrap' : 'wrap'};
  justify-content: ${props => props.justify};
  align-items: ${props => props.align};
  gap: ${props => props.gap}px;
  width: ${props => props.fullwidth && '100%'};
  height: ${props => props.fullHeight && '100%'};
`

const Grid = (props: React.PropsWithChildren<GridProps>) => {
  const {
    direction = 'row', gap = 0, justify = 'flex-start', align = 'stretch', fullwidth = false, fullHeight = false,
    noWrap = false, style,
    children,
  } = props

  return (
    <Root
      direction={direction}
      gap={gap}
      justify={justify}
      align={align}
      fullwidth={fullwidth}
      fullHeight={fullHeight}
      noWrap={noWrap}
      style={style}
    >
      {children}
    </Root>
  )
}

type Sizes = {
  [key in GridSize]?: number
}

type ColumnProps = GridColProps & Sizes

const sizes: GridSize[] = ['xs', 'sm', 'md', 'lg', 'xl']

const Column = styled.div<ColumnProps>`
  position: relative;
  
  ${props => {
  return sizes.map((size: GridSize) => {
    const width = 100 / (gridColumnCount / (props[size]!))

    if (!isNaN(width)) {
      return css`
          @media (min-width: ${props.theme.breakpoints[size!]}px) {
            width: ${width + '%'};
          }
        `
    }
  })
}}
  overflow-y: auto;
`

interface GridColProps extends Sizes {
  style?: React.CSSProperties
}

Grid.Col = (props: React.PropsWithChildren<GridColProps>) => {
  const { xs, sm, md, lg, xl, style, children } = props

  function checkSizes() {
    const sizeProps = [xs, sm, md, lg, xl]

    sizeProps.forEach((s: number | undefined, idx) => {
      if (!s) {
        return
      }
      if (s > 12 || s < 1) {
        console.error(`Grid.Col: "${sizes[idx]}" prop must be between 1 and 12`)
      }
    })
  }

  checkSizes()

  return <Column xs={xs} sm={sm} md={md} lg={lg} xl={xl} style={style}>{children}</Column>
}

export { Grid }